import { UseFormReturn } from 'react-hook-form';

import { ContractData } from '@shared/types/contract-data';

import { PricedItem } from '@shared/types/priced-item';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@web/components/ui/form';
import { useCallback, useEffect, useState } from 'react';
import PriceTable from './price-table';

import { DoctorDataForContract } from '@shared/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@web/components/ui/select';
import { PriceBreakdown } from './total-price-breakdown';

export function ContractDetailsView({
  form,
  allProcedures,
  allProducts,
  allDoctors,
}: {
  form: UseFormReturn<ContractData>;
  allProcedures: PricedItem[];
  allProducts: PricedItem[];
  allDoctors: DoctorDataForContract[];
}) {
  const procedures = form.watch('procedures');

  const setProcedures = useCallback(
    (procedures: PricedItem[]) => {
      form.setValue('procedures', procedures);
    },
    [form],
  );

  const [surgeonId] = useState(
    form.getValues('surgeonId') ? form.getValues('surgeonId') : 'null',
  );

  const products = form.watch('products');

  const setProducts = useCallback(
    (products: PricedItem[]) => {
      form.setValue('products', products);
    },
    [form],
  );

  useEffect(() => {
    const surgeon = allDoctors.find((d) => d.id === surgeonId);
    if (surgeon) {
      form.setValue(
        'surgeonDisplayName',
        `${surgeon.name} - ${surgeon.brandName}`,
      );
    }
  }, [allDoctors, form, surgeonId]);

  const setSurgeonId = useCallback(
    (surgeonId: string) => {
      form.setValue('surgeonId', surgeonId);
      const surgeon = allDoctors.find((d) => d.id === surgeonId);
      if (surgeon) {
        form.setValue(
          'surgeonDisplayName',
          `${surgeon.name} - ${surgeon.brandName}`,
        );
      }
    },
    [allDoctors, form],
  );

  const totalProcedures = procedures.reduce((acc, p) => acc + p.price, 0);
  const totalProducts = products.reduce((acc, p) => acc + p.price, 0);
  const subTotal = totalProcedures + totalProducts;

  form.setValue('subTotal', subTotal);

  // const [subTotal, setSubTotal] = useState(totalProcedures + totalProducts); // Example subtotal

  const setDiscountValue = useCallback(
    (discount: number) => {
      form.setValue('discount', discount);
    },
    [form],
  );

  const setTotalValue = useCallback(
    (discount: number) => {
      form.setValue('totalCost', discount);
    },
    [form],
  );

  const handleValuesChange = (updatedValues: {
    discount: number;
    total: number;
  }) => {
    console.log('Values changed:', updatedValues);
    setDiscountValue(updatedValues.discount);
    setTotalValue(updatedValues.total);
  };

  return (
    <div className="relative flex flex-col gap-4">
      <div className="flex justify-end">
        <FormField
          control={form.control}
          name="surgeonId"
          render={() => (
            <FormItem className="flex items-center gap-2">
              <Select onValueChange={setSurgeonId} defaultValue={surgeonId}>
                <FormControl>
                  <SelectTrigger className="w-52">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {allDoctors.map((doctor) => (
                    <SelectItem key={doctor.id} value={doctor.id}>
                      {doctor.name}{' '}
                      {doctor.brandName ? `- (${doctor.brandName})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>{' '}
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4">
        <div className="rounded-sm shadow-sm">
          <PriceTable
            items={procedures}
            setItems={setProcedures}
            allItems={allProcedures}
            itemType="procedure"
          />
        </div>
        <div className="rounded-sm shadow-sm">
          <PriceTable
            items={products}
            setItems={setProducts}
            allItems={allProducts}
            itemType="product"
          />
        </div>
      </div>

      {/* <FormField
        control={form.control}
        name="totalCost"
        render={({ field }) => (
          <FormItem className="absolute right-0 top-0 flex items-center gap-2">
            <FormLabel>Total</FormLabel>
            <FormControl>
              <Input
                type="number"
                className="w-24"
                value={field.value ?? subTotal}
                onChange={(e) => {
                  field.onChange(parseInt(e.target.value));
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      /> */}

      <div className="mt-10 flex w-full justify-end">
        <PriceBreakdown
          subTotal={subTotal}
          initialDiscount={+form.getValues('discount')}
          onValuesChange={handleValuesChange}
          isEditable={true}
        />
      </div>
    </div>
  );
}
